import React from "react";
import { motion } from "framer-motion";
import ApiLoader from "../ui/ApiLoader";
import { DataTable } from "../common/DataTable";

const SLA_BADGE_CLASS = {
  CRITICAL: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
  HIGH: "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200",
  MEDIUM: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
  LOW: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
};

const SLA_LIMITS = [
  { severity: "CRITICAL", limit: "24 hours" },
  { severity: "HIGH", limit: "72 hours (3 days)" },
  { severity: "MEDIUM", limit: "168 hours (7 days)" },
  { severity: "LOW", limit: "336 hours (14 days)" },
];

const AnalyticsSLAComponent = ({ loading, analyticsData, slaBreaches }) => {
  return (
    <motion.div
      key="analytics"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {loading && !analyticsData ? (
        <ApiLoader label="Loading analytics..." />
      ) : (
        <>
          {/* Analytics Metrics */}
          {analyticsData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resolution Time</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {analyticsData.average_resolution_time_hours?.toFixed(1) || 0}h
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Average resolution time</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">MTTR Trend</h3>
                <p className="text-3xl font-bold text-green-600">
                  {analyticsData.mttr_improvement_percentage?.toFixed(1) || 0}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">MTTR improvement</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Response Efficiency</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {analyticsData.response_efficiency_score?.toFixed(1) || 0}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Response efficiency</p>
              </div>
            </div>
          )}

          {/* SLA Time Limits */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">SLA Time Limits</h3>
            <DataTable
              columns={[
                {
                  accessorKey: "severity",
                  header: "Severity",
                  cell: ({ getValue }) => (
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${SLA_BADGE_CLASS[getValue()]}`}>
                      {getValue()}
                    </span>
                  ),
                },
                {
                  accessorKey: "limit",
                  header: "SLA Time Limit",
                  cell: ({ getValue }) => <span className="text-sm text-gray-600 dark:text-gray-400">{getValue()}</span>,
                },
              ]}
              data={SLA_LIMITS}
              getRowId={(row) => row.severity}
              enableColumnVisibility={false}
            />
          </div>

          {/* SLA Breaches */}
          {slaBreaches?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active SLA Breaches</h3>
              <div className="space-y-3">
                {slaBreaches.map((breach, index) => (
                  <div key={index} className="p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{breach.title || "N/A"}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{breach.status || "N/A"}</p>
                        <p className="text-sm text-gray-400 dark:text-gray-200">{breach.severity || "N/A"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                          {breach.hours_overdue?.toFixed(1)}h overdue
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Severity: {breach.severity?.toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default AnalyticsSLAComponent;
